import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { WhatsAppButton } from "@/pages/DirectorioDetalle";
import { toast, Toaster } from "sonner";
import {
  Search,
  Filter,
  MapPin,
  Phone,
  Mail,
  BadgeCheck,
  ArrowRight,
  Loader2,
  Users,
  ChevronLeft,
  ChevronRight,
  XCircle,
} from "lucide-react";

const PER_PAGE = 12;
const ALL = "__todos__";

export default function Directorio() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [catalogos, setCatalogos] = useState({ especialidades: [], estados: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const qParam = searchParams.get("q") || "";
  const espParam = searchParams.get("especialidad") || "";
  const estParam = searchParams.get("estado") || "";
  const pageParam = Number(searchParams.get("page") || "1") || 1;

  const [q, setQ] = useState(qParam);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (qParam) params.set("q", qParam);
      if (espParam) params.set("especialidad", espParam);
      if (estParam) params.set("estado", estParam);
      params.set("page", String(pageParam));
      params.set("per_page", String(PER_PAGE));
      const { data } = await api.get(`/directorio?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [qParam, espParam, estParam, pageParam]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/directorio/catalogos");
        setCatalogos(data);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const submitQuery = (e) => {
    e?.preventDefault();
    setFilter("q", q.trim());
  };

  const clearFilters = () => {
    setQ("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const gotoPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const anyFilter = qParam || espParam || estParam;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
        {/* HERO */}
        <section className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
            <Users size={12} /> Directorio público · sin registro
          </div>
          <h1
            className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-[#0F2942] tracking-tight"
            data-testid="directorio-title"
          >
            Especialistas verificados en México
          </h1>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
            Médicos con perfil profesional completo y suscripción activa en Siempre Salud. Cada
            ficha incluye cédula profesional, especialidad y datos de contacto para consulta
            directa.
          </p>
        </section>

        {/* FILTERS */}
        <section
          className="rounded-xl border border-border bg-white p-4 sm:p-5"
          data-testid="directorio-filtros"
        >
          <form onSubmit={submitQuery} className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre, especialidad o ciudad…"
                className="pl-9"
                data-testid="directorio-buscador"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 lg:flex lg:gap-3">
              <Select
                value={espParam || ALL}
                onValueChange={(v) => setFilter("especialidad", v === ALL ? "" : v)}
              >
                <SelectTrigger
                  className="lg:w-52"
                  data-testid="directorio-filter-especialidad"
                >
                  <SelectValue placeholder="Especialidad" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value={ALL} data-testid="filter-especialidad-todas">
                    Todas
                  </SelectItem>
                  {(catalogos.especialidades || []).map((e) => (
                    <SelectItem key={e} value={e} data-testid={`filter-esp-${slug(e)}`}>
                      {e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={estParam || ALL}
                onValueChange={(v) => setFilter("estado", v === ALL ? "" : v)}
              >
                <SelectTrigger
                  className="lg:w-52"
                  data-testid="directorio-filter-estado"
                >
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value={ALL} data-testid="filter-estado-todos">
                    Todos
                  </SelectItem>
                  {(catalogos.estados || []).map((e) => (
                    <SelectItem key={e} value={e} data-testid={`filter-est-${slug(e)}`}>
                      {e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                data-testid="directorio-search-btn"
              >
                <Filter size={14} /> Filtrar
              </Button>
              {anyFilter && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearFilters}
                  data-testid="directorio-clear-btn"
                >
                  <XCircle size={14} /> Limpiar
                </Button>
              )}
            </div>
          </form>

          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2">
            <BadgeCheck size={12} className="text-[#2563EB]" />
            {loading
              ? "Cargando resultados…"
              : `${total} médico${total === 1 ? "" : "s"} visible${total === 1 ? "" : "s"}`}
          </div>
        </section>

        {/* RESULTS */}
        <section className="mt-6" data-testid="directorio-lista">
          {loading ? (
            <div className="rounded-xl border border-border bg-white p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Consultando el directorio…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm">
              {error}
            </div>
          ) : items.length === 0 ? (
            <div
              className="rounded-xl border border-border bg-white p-8 text-center"
              data-testid="directorio-empty"
            >
              <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center text-[#0F2942] mx-auto">
                <Search size={18} />
              </div>
              <h3 className="mt-3 font-display text-lg font-semibold text-[#0F2942]">
                Sin resultados
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Ajusta los filtros o intenta un término más general. Si eres médico y quieres
                aparecer aquí, crea tu cuenta y activa tu suscripción.
              </p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <Link to="/registro">
                  <Button className="bg-[#0F2942] hover:bg-[#123553] text-white">
                    Soy médico
                  </Button>
                </Link>
                <Button variant="outline" onClick={() => navigate("/")}>
                  Volver al inicio
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((m) => (
                <DoctorCard key={m.id} m={m} />
              ))}
            </div>
          )}
        </section>

        {/* PAGINATION */}
        {pages > 1 && (
          <nav className="mt-6 flex items-center justify-center gap-2" data-testid="directorio-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={pageParam <= 1}
              onClick={() => gotoPage(pageParam - 1)}
              data-testid="directorio-prev"
            >
              <ChevronLeft size={14} /> Anterior
            </Button>
            <span className="text-xs text-muted-foreground px-2">
              Página {pageParam} de {pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pageParam >= pages}
              onClick={() => gotoPage(pageParam + 1)}
              data-testid="directorio-next"
            >
              Siguiente <ChevronRight size={14} />
            </Button>
          </nav>
        )}
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function DoctorCard({ m }) {
  const especialidadLabel = useMemo(
    () => (m.especialidad === "Otra" && m.especialidad_otra ? m.especialidad_otra : m.especialidad),
    [m]
  );
  const hasPhone = /^\d{10}$/.test((m.telefono || "").replace(/\D/g, ""));
  return (
    <div
      className="group rounded-xl border border-border bg-white p-5 flex flex-col transition-colors hover:border-[#2563EB]/40"
      data-testid={`directorio-card-${m.id}`}
    >
      <Link to={`/directorio/${m.id}`} className="flex-1">
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 shrink-0 rounded-lg bg-[#0F2942] text-white grid place-items-center font-display font-semibold"
            aria-hidden
          >
            {m.iniciales || "MD"}
          </div>
          <div className="min-w-0">
            <div className="font-display text-base font-semibold text-[#0F2942] leading-tight truncate">
              {m.nombre_completo}
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] mt-0.5">
              {especialidadLabel}
            </div>
          </div>
        </div>

        <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
          <li className="flex items-center gap-1.5">
            <MapPin size={12} className="text-[#2563EB]" />
            {m.ciudad}, {m.estado}
          </li>
          <li className="flex items-center gap-1.5">
            <Phone size={12} className="text-[#2563EB]" />
            <span className="tabular-nums">{formatPhone(m.telefono)}</span>
          </li>
          <li className="flex items-center gap-1.5 truncate">
            <Mail size={12} className="text-[#2563EB] shrink-0" />
            <span className="truncate">{m.correo_contacto}</span>
          </li>
        </ul>
      </Link>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
        <div className="text-[11px] text-muted-foreground shrink-0">
          Céd. <span className="font-mono text-foreground">{m.cedula_profesional}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasPhone && <WhatsAppButton medico={m} compact />}
          <Link
            to={`/directorio/${m.id}`}
            className="text-xs font-medium text-[#2563EB] inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform"
          >
            Ficha <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function formatPhone(t) {
  if (!t || t.length !== 10) return t || "";
  return `${t.slice(0, 2)} ${t.slice(2, 6)} ${t.slice(6)}`;
}

function slug(s) {
  return (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
